import { type FC, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMaxProduction } from '@common/hooks/max-production';
import { getImage } from '@common/lib/images';
import { type GoodsType } from '@common/types/goods';
import { type PlantTypeText } from '@common/types/plant';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import { H3, StringMain } from '@components/Typography/Typography';
import {
  MainButton,
  type MainButtonProps,
  MotionBlueButton,
} from '@components/ui/Button/Button';
import { Picture } from '@components/ui/Picture/Picture';
import cn from 'classnames';
import {
  animate,
  motion,
  useMotionTemplate,
  useMotionValue,
  type ValueAnimationTransition,
} from 'framer-motion';
import mergeRefs from 'merge-refs';

import css from './styles.module.scss';

interface ProductionButtonProps {
  localStock: number;
  toAdd: number;
  itemName?: GoodsType | PlantTypeText;
  claimButtonProps: Partial<MainButtonProps>;
  produceButtonProps: {
    onClick: () => void;
    disabled: boolean;
    inactive: boolean;
    isLoading: boolean;
  };
  producted: number;
  recipe?: Record<string, number>;
}

const ProductionButton: FC<ProductionButtonProps> = memo(
  ({
    localStock,
    itemName,
    toAdd,
    claimButtonProps,
    produceButtonProps,
    producted,
    recipe,
  }) => {
    const { t } = useTranslation();
    const max = useMaxProduction(recipe);
    const ref12 = useHighlightComponentRect(12);
    const ref17 = useHighlightComponentRect<HTMLButtonElement>(17);
    const ref21 = useHighlightComponentRect(21);
    const firstColor = useMotionValue('#4089ee99');
    const secondColor = useMotionValue('#0a4eb399');
    const firstBlockLeftPos = useMotionValue('-14%');
    const secondBlockLeftPos = useMotionValue('-64%');
    const backgroundImage = useMotionTemplate`linear-gradient(180deg, ${firstColor} 0%, ${secondColor} 100%)`;
    const produceButtonid =
      itemName === 'lumber' ? 'intro-step-12' : 'intro-step-21';

    useEffect(() => {
      const firstTransition: ValueAnimationTransition<string> = {
        delay: 0.8,
        duration: 0.5,
        ease: 'linear',
      };

      const secondTransition: ValueAnimationTransition<string> = {
        duration: 0.5,
        ease: 'linear',
      };
      let timerId: NodeJS.Timeout | undefined;
      const animation = async (): Promise<void> => {
        await Promise.all([
          animate(firstColor, '#4089EE', firstTransition),
          animate(secondColor, '#0A4EB3', firstTransition),
          animate(firstBlockLeftPos, '113%', firstTransition),
          animate(secondBlockLeftPos, '108%', firstTransition),
        ]);
        await Promise.all([
          animate(firstColor, '#4089ee99', secondTransition),
          animate(secondColor, '#0a4eb399', secondTransition),
        ]);
        firstBlockLeftPos.set('-14%');
        secondBlockLeftPos.set('-64%');
      };

      if (!(produceButtonProps.inactive || toAdd > max)) {
        animation();
        timerId = setInterval(() => {
          animation();
        }, 4000);
      }

      return (): void => {
        clearInterval(timerId);
      };
    }, [produceButtonProps.inactive, toAdd, max]);

    const image = itemName
      ? getImage({ name: itemName, field: 'icon' })
      : undefined;

    return (
      <div className="flex gap-2 h-14 relative">
        <MainButton
          ref={ref17}
          id="intro-step-17"
          {...claimButtonProps}
          className="!h-full flex-1 flex flex-col !items-start px-4"
          text=""
        >
          <StringMain weight="bold" className="mb-[2px]">
            {t('Claim')}
          </StringMain>

          <H3
            weight="extrabold"
            className={cn(
              claimButtonProps.inactive ? '!text-white/40' : css.greenText,
            )}
          >
            {localStock + producted}
          </H3>
        </MainButton>
        <div className="absolute z-[1] inset-0 m-auto w-10 h-10 rounded-full bg-main-bg">
          {itemName && (
            <Picture
              className="w-full h-full"
              img={image?.img}
              webp={image?.webp}
              alt={itemName}
            />
          )}
        </div>
        <MotionBlueButton
          {...produceButtonProps}
          inactive={produceButtonProps.inactive || toAdd > max}
          style={{
            backgroundImage:
              produceButtonProps.inactive || toAdd > max
                ? 'none'
                : backgroundImage,
          }}
          className={css.productionButton}
        >
          <div
            ref={mergeRefs(ref12, ref21)}
            id={produceButtonid}
            className="absolute inset-0 rounded-lg"
          >
            <div className="relative w-full h-full rounded-lg overflow-hidden">
              <motion.span
                style={{ left: firstBlockLeftPos }}
                className={css.firstLine}
              ></motion.span>
              <motion.span
                style={{ left: secondBlockLeftPos }}
                className={css.secondLine}
              ></motion.span>
            </div>
          </div>
          <StringMain weight="bold" className="mb-[2px]">
            {t('Produce')}
          </StringMain>
          <H3 weight="extrabold">{`+${toAdd}`}</H3>
        </MotionBlueButton>
      </div>
    );
  },
);

export { ProductionButton };
